import type * as ents from '@attain-apps/entities'

import type * as admin from '../../admin'

import type { Feature } from './feature'
import * as models from './models'

/**
 * Selectors for the agent-assignment workflow state.
 */
export class Selectors {
  /**
   * Represents the list of users available for assignment for the currently
   * selected entity.
   */
  public readonly users
  /**
   * List of currently selected user ids ready for assignment
   */
  public readonly selectedUserIds
  /**
   * List of selected users ready for assignment
   */
  public readonly selectedUsers
  /**
   * List of currently selected agent ids ready for assignment
   */
  public readonly selectedAgentIds
  /**
   * Returns true if assignments are being edited
   */
  public readonly isEditing
  /**
   * Current access level in editing mode
   */
  public readonly access
  /**
   * Agent access flags available for the selected users.
   */
  public readonly availableAccessFlags
  /**
   * List of assignments ready for api request (cartesian product of users × agents)
   */
  public readonly assignments
  /**
   * User that is marked for unassignment
   */
  public readonly markedUserForUnassignment
  /**
   * Message for the latest failed agent-assignment mutation, if any.
   */
  public readonly mutationErrorMessage
  /**
   * @see {@link ents.core.admin.Access.Assignment} that is in the process of
   * deletion
   */
  public readonly deletingAssignment
  /**
   * Page aliases for agent page context
   */
  public readonly pageAliases
  /**
   * Whether agent is for non-prebuilt pages
   */
  public readonly isForNonPrebuiltPages

  constructor(feature: Feature, contextsFeature: admin.contexts.Feature) {
    this.users = feature.state.project(state => state.users)
    this.selectedUserIds = feature.state.project(state => state.selectedUserIds)
    this.selectedUsers = this.users.project(
      this.selectedUserIds.asSelector,
      (users, selectedUserIds) =>
        users.filter(user => selectedUserIds.includes(user.id)),
    )
    this.selectedAgentIds = feature.state.project(
      state => state.selectedAgentIds,
    )
    this.isEditing = feature.state.project(state => state.isEditing)
    this.availableAccessFlags = this.selectedUsers.project(
      models.User.getAccessFlags,
    )
    this.access = feature.state
      .project(state => state.access)
      .project(
        this.availableAccessFlags.asSelector,
        models.User.limitAccessToFlags,
      )
    this.pageAliases = feature.state.project(state => state.pageAliases)
    this.isForNonPrebuiltPages = feature.state.project(
      state => state.isForNonPrebuiltPages,
    )
    this.assignments = this.selectedUserIds.project(
      this.selectedAgentIds.asSelector,
      this.access.asSelector,
      contextsFeature.selectors.activeEntityContext.asSelector,
      this.pageAliases.asSelector,
      this.isForNonPrebuiltPages.asSelector,
      (
        userIds,
        agentIds,
        access,
        context,
        pageAliases,
        isForNonPrebuiltPages,
      ) =>
        userIds.flatMap(userId =>
          agentIds.map(agentId => ({
            ...context,
            access,
            agentId,
            pageContext:
              pageAliases || isForNonPrebuiltPages
                ? {
                    isForNonPrebuiltPages,
                    pageAliases: pageAliases
                      ? Array.from(pageAliases)
                      : undefined,
                  }
                : undefined,
            userId,
          })),
        ),
    )
    this.markedUserForUnassignment = feature.state.project(
      state => state.markedUserForUnassignment,
    )
    this.mutationErrorMessage = feature.state.project(
      state => state.mutationError?.message,
    )
    this.deletingAssignment = this.markedUserForUnassignment.project(
      feature.state.project(state => state.markedAgentIdForUnassignment)
        .asSelector,
      contextsFeature.selectors.activeEntityContext.asSelector,
      (user, agentId, context) =>
        context && user?.id && agentId
          ? {
              ...context,
              access: 0,
              agentId,
              userId: user.id,
            }
          : undefined,
    )
  }
}
